import { useValidation } from "Hooks/useValidation";
import { useContext, useEffect } from "react";
import alertToast from "Util/ToastifyUtils";
import { isEmail } from "Validators/Functions/Email";
import ComboboxSetter from "Views/Components/Combobox/ComboboxSetter";
import { TextFieldSetter } from "Views/Components/TextBox/TextFieldSetter";
import { ESignEnvelopeCreateContext, ESignEnvelopeCreateDispatchContext, RecipientDetail } from "../ESignEnvelopeCreate";

export const ESignSenderInformationStage = () => {
	const { data: { templateId, senderName, senderCompany, notificationEmail }, availableTemplates } = useContext(ESignEnvelopeCreateContext);
	const dispatch = useContext(ESignEnvelopeCreateDispatchContext);

	const handleTemplateChange = (selectedTemplateId: string) => {
		const selectedTemplate = availableTemplates.find(item => item.id === selectedTemplateId);
		if (!selectedTemplate) {
			alertToast("We're unable to find the selected document. Please refresh your browser and try again.", "error");
			return;
		}

		// update the recipient details as per the selected template
		const recipientDetails: RecipientDetail[] = Array.from({ length: selectedTemplate.numberOfSignee }, (_, index) => index + 1).map(recipientIndex => {
			return  {
				name: "",
				email: "",
				phone: "",
				preFillValues: (selectedTemplate.templateContent.preFillFieldsForSignee[`signee-${recipientIndex}`] || []).reduce((acc, field) => {
					acc[field] = "";
					return acc;
				}, {} as { [key: string]: string })
			} as RecipientDetail;
		});

		// set the prefill values for the template
		const prefillValuesAsPerTemplate: { [key: string]: string } = {};
		selectedTemplate.templateContent.inputFields.forEach(field => {
			if (field.startsWith("common-field-")) {
				const fieldName = field.replace("common-field-", "");
				prefillValuesAsPerTemplate[fieldName] = "";
			}
		});

		dispatch({
			type: "SetRequestData",
			payload: {
				templateId: selectedTemplateId,
				prefillValues: prefillValuesAsPerTemplate,
				recipients: recipientDetails,
			}
		});
	}

	const validation = useValidation({
		"senderCompany": {
			value: senderCompany,
			isRequired: true,
			requiredText: "Please enter a company name.",
		},
		"senderName": {
			value: senderName,
			isRequired: true,
			requiredText: "Please enter a name.",
			validate: v => v.assertRule(r => !!r, "Please enter a name."),
		},
		"notificationEmail": {
			value: notificationEmail,
			isRequired: true,
			requiredText: "Please enter an email address.",
			validate: v => v.assertTruthy("Please enter an email address.") && v.assertRule(r => !!r && isEmail(r), "Please enter a valid email address."),
		},
		"templateId": {
			value: templateId,
			isRequired: true,
			requiredText: "Please select a document template.",
			validate: v => v.assertRequired("Please select a document template.") && v.assertRule(r => !!r, "Please select a document template."),
		}
	});

	useEffect(() => {
		dispatch({ type: "StageValidation", payload: { stage: "sender-information", isValid: validation.isValid } });
	}, [validation.isValid, dispatch]);

	return (
		<div>
			<TextFieldSetter
				label="Company name"
				value={senderCompany}
				setValue={value => {
					dispatch({ type: "SetSenderCompany", payload: { senderCompany: value } });
				}}
				isRequired
				tooltip="The company name will be displayed in the ESignature workflow and notifications."
				errors={validation.get("senderCompany")[0]}
			/>
			<TextFieldSetter
				label="Name"
				value={senderName}
				setValue={value => {
					dispatch({ type: "SetSenderName", payload: { senderName: value } })
				}}
				tooltip="The sender's name will be displayed in the ESignature workflow and notifications."
				isRequired
				errors={validation.get("senderName")[0]}
			/>
			<TextFieldSetter
				label="Notification email"
				value={notificationEmail}
				setValue={value => {
					dispatch({ type: "SetNotificationEmail", payload: { notificationEmail: value } })
				}}
				tooltip="The email address where you will receive notifications about this ESignature workflow."
				isRequired
				errors={validation.get("notificationEmail")[0]}
			/>
			{availableTemplates.length === 0 && (
				<div className="alert alert__warning">
					<p>No ESignature templates found. Please create a template to start a new ESignature workflow.</p>
				</div>
			)}
			<ComboboxSetter
				value={templateId}
				setValue={handleTemplateChange}
				label="Choose a document"
				isRequired
				options={availableTemplates.map(item => ({
					display: item.name,
					value: item.id,
				}))}
				getOptionValue={(id?: string) => id}
				tooltip="Select a document template to start the ESignature workflow. The template will define the recipients and their required fields."
				errors={validation.get("templateId")[0]}
			/>
		</div>
	)
}